import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export const SEOHead = ({
  title = 'David Isei · Hawaiʻi healthcare, measured',
  description = 'Executive Director of the Hawaiʻi Healthcare Task Force and builder of the Hawaiʻi Healthcare Intelligence Platform. Island-level findings on workforce, network adequacy, and access, with every figure sourced, dated, and corrected in public.',
  keywords = ['Hawaiʻi healthcare', 'physician shortage', 'network adequacy', 'rural health', 'RHTP', 'health data'],
  image = '/og-image.jpg',
  url = 'https://davidisei.com',
  type = 'website',
  author = 'David Isei',
  publishedTime,
  modifiedTime
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  
  const siteName = 'David Isei - Executive Leadership';
  const twitterHandle = '@davidisei';
  
  // Construct full URLs
  const siteOrigin = 'https://davidisei.com';
  const fullImageUrl = image.startsWith('http') ? image : `${siteOrigin}${image.startsWith('/') ? '' : '/'}${image}`;
  const canonicalUrl = url.startsWith('http') ? url : `${siteOrigin}${url.startsWith('/') ? '' : '/'}${url}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={i18n.language} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:alt" content={`${author} - Professional Portrait`} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={i18n.language === 'ja' ? 'ja_JP' : 'en_US'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta name="twitter:image:alt" content={`${author} - Professional Portrait`} />
      
      {/* Article specific tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && (
        <meta property="article:author" content={author} />
      )}
      
      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#00254d" />
      <meta name="msapplication-TileColor" content="#00254d" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      
    </Helmet>
  );
};